import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery
  } from 'redux-saga/effects';

import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { deleteContentAction } from '~src/spacesPlatform/actions/DeleteContentAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { siteUrlsSelector } from '../../bootstrap/redux/selectors/SiteUrlsSelectors';
import { Messages } from '../../codegen/Localize';
import { ModalInstance, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { DeleteContentsAction } from '../../spacesPlatform/actions/DeleteContentsAction';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import {
  HideMetricsBanner,
  HideMetricsBannerAction,
  ResumeMetric,
  resumeMetric,
  ResumeMetricAction as ResumeMetricReduxAction,
  ResumeMetricFromExplorer,
  ResumeMetricFromExplorerAction,
} from '../actions/UpdateMetricsActions';
import { ResumeMetricErrorDialogProps, resumeMetricErrorModalOptions } from '../components/dialog/ResumeMetricErrorDialog';
import { IMetricInfo } from '../models/IMetricInfo';
import { Metrics } from '../resources/Metrics';
import { SetShowMetricsBannerRequest } from '../serverApi/requests/SetShowMetricsBannerRequest';
import { ResumeMetricAction } from '../services/ResumeMetricAction';
import { metricsDataSaga } from './MetricsDataSaga';

export function* handleResumeMetric(action: ResumeMetricReduxAction) {
  try {
    yield call(resumeSuspendedMetric, action.metric, action.isOwner);
    yield call(refreshAfterMetricsActionSucceeded);
  } catch(e) {
    yield call(deleteSuspendedMetric, action.metric, e.message);
  }
}

export function* handleResumeMetricFromExplorer(action: ResumeMetricFromExplorerAction): SagaIterator {
  try {
    const [ metricInfo ] = (yield all([
      call(getMetricInfo, action.metricId),
      call(navigateToMetricDetailPage, action.metricId)
    ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
    yield put(resumeMetric(metricInfo, action.isOwner));
  } catch (e) {
    yield put(requestFailed(e, Messages.unknownFailure()));
  }
}

export function* handleHideMetricsBanner(action: HideMetricsBannerAction) {
  try {
    yield call(dismissMetricsBanner);
    yield call(updateSessionInfo);
  } catch (e) {
    yield put(requestFailed(e, Messages.unknownFailure()));
  }
}

export function resumeSuspendedMetric(metric: IMetricInfo, isOwner: boolean) {
  return ResumeMetricAction.resumeMetric(metric, isOwner);
}

export function* refreshAfterMetricsActionSucceeded() {
  yield put(requestSpaceContents());
}

export function* deleteSuspendedMetric(metric: IMetricInfo, errorMessage: string) {
  try {
    const shouldDelete: boolean = yield call(openMetricResumeErrorDialog, errorMessage);
    if (!shouldDelete) {
      return;
    }
    yield put(deleteContentAction(metric as IContentInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

export function confirmDeleteMetric(metric: IMetricInfo) {
  return DeleteContentsAction.confirmDeleteContents([metric as IContentInfo]);
}

export function openMetricResumeErrorDialog(errorMessage: string): Promise<boolean> {
  let modalInstance: ModalInstance<boolean>;

  const props: ResumeMetricErrorDialogProps = {
    submit: () => modalInstance && modalInstance.close(true),
    cancel: () => modalInstance && modalInstance.close(false),
    errorMessage: errorMessage
  };

  modalInstance = ReactModalService.open(resumeMetricErrorModalOptions(props));
  return modalInstance.result;
}

export function* navigateToMetricDetailPage(metricId: string) {
  const urls: SiteUrls = yield select(siteUrlsSelector);
  const metricPath = urls.metricUrl(metricId);
  WindowLocationService.navigateTo(metricPath);
  WindowLocationService.updateHashQueryParameterWithReplace(UrlParamKeys.SuppressResumeMetricBanner, 't');
}

export async function getMetricInfo(metricId: string): Promise<IMetricInfo> {
  return await Metrics.getById(metricId);
}

export function dismissMetricsBanner() {
  return WebHttpClient.sendRequest(new SetShowMetricsBannerRequest({ showMetricsBanner: false }));
}

export function updateSessionInfo() {
  const sessionManagementService = getSessionManagementServiceInstance();
  return sessionManagementService.refreshSessionInfo();
}

export function* rootMetricSpaceSagas(): SagaIterator {
  yield takeEvery(ResumeMetric, handleResumeMetric);
  yield takeEvery(ResumeMetricFromExplorer, handleResumeMetricFromExplorer);
  yield takeEvery(HideMetricsBanner, handleHideMetricsBanner);
  yield fork(metricsDataSaga);
}
